import clsx from 'clsx';
import { decodeHtml, getFallbackImg, isUserAuthenticated } from '../../utils/OfferUtils';
import config from '../../lib/config';
import { PANORAMIC_ASPECT_RATIO } from '../../constants/OffersHeroConstants';

import { Types, Image, Button, Heading, tags, useDocumentDirection } from '@marriott/mi-ui-library';
import { OffersMBEPropertyProps } from './OffersHero.types';
import { StyledOffersHeroMBE } from './OffersHero.styles';
import { useEffect, useRef, useState } from 'react';
import { IMAGE_CACHE_DOMAIN, IMAGE_CACHE_DOMAIN_CN } from '../../constants/CommonConstants';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { shouldUseMockData } from '../../utils/CommonUtils';
import { useSession } from '@marriott/mi-headless-utils';

export const OffersMBE: React.FC<OffersMBEPropertyProps> = props => {
  const { isAuthorMode } = props;
  const { datalayer } = useSession();
  const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean>(isUserAuthenticated(datalayer));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [mockPropsData, setMockPropsData] = useState<any>({});
  const OfferObj = !isAuthorMode
    ? props?.offersMBEProps?.responseObject?.offers?.mbeOfferById?.offers
    : mockPropsData?.responseObject?.offers?.mbeOfferById?.offers;
  const CardLinkPrimaryBtnRef = useRef(null);
  const styleclass = props?.modelObj?.styleclass;
  const dir = useDocumentDirection();

  const [mQuery, setMQuery] = useState(true);
  useEffect(() => {
    handleScreenResize();
    window.addEventListener('resize', handleScreenResize);
  }, []);

  // Get mock data for author mode
  const GetMockData = async (isAuthorMode: boolean) => {
    if (shouldUseMockData(isAuthorMode)) {
      const mockData = await import('./__mock__/OffersHero.mock');
      return mockData?.mockOffersMBEProps?.offersMBEProps;
    }
  };
  useEffect(() => {
    if (isAuthorMode) {
      const fetchMockData = async () => {
        const mockData = await GetMockData(isAuthorMode);
        setMockPropsData(mockData);
      };
      fetchMockData();
    }
  }, [isAuthorMode]);

  useEffect(() => {
    setIsUserLoggedIn(isUserAuthenticated(datalayer));
  }, [datalayer]);

  const handleScreenResize = () => {
    const mobileSize = window.matchMedia('(max-width: 767px)');
    const tabletSize = window.matchMedia('(min-width:768px)');
    if (mobileSize.matches) {
      setMQuery(true);
    } else if (tabletSize.matches) {
      setMQuery(false);
    }
  };

  const imageAltText = OfferObj?.media?.primaryImage?.caption;
  const imageTitle = OfferObj?.media?.primaryImage?.title;

  const { envVarsObject } = useClientEnvVarsStore();
  const imageDomain = envVarsObject?.['UXL_IMAGE_CACHE_DOMAIN'] ?? IMAGE_CACHE_DOMAIN;
  const isChinaDomain = imageDomain === IMAGE_CACHE_DOMAIN_CN;
  let desktopImageSrc = '';

  const fallBackImg = getFallbackImg(
    isChinaDomain ? config.FALLBACK_CN_IMG_URI : config.FALLBACK_IMG_URI,
    PANORAMIC_ASPECT_RATIO
  );
  // const OffersHeroSPORef = useRef(null);
  const ASPECT_RATIO_UXL_FIELD_MAPPING = {
    PANO: 'panoramicHorizontal',
  };
  const objKey = PANORAMIC_ASPECT_RATIO as keyof typeof ASPECT_RATIO_UXL_FIELD_MAPPING;
  const imageUrls = OfferObj?.media?.primaryImage?.imageUrls;
  const DACImagePath = imageUrls?.[ASPECT_RATIO_UXL_FIELD_MAPPING?.[objKey]];

  // const bannerTextClassName = clsx('t-color-white t-font-swiss721');
  if (DACImagePath) {
    desktopImageSrc = imageDomain + DACImagePath;
  } else {
    desktopImageSrc = imageDomain + fallBackImg;
  }

  const header = decodeHtml(OfferObj?.title);
  const description = decodeHtml(OfferObj?.description);
  const headerTag = props?.modelObj?.headerTag ?? tags.h2;

  const hasPrimaryButton =
    props?.modelObj?.primaryCtaText &&
    props?.modelObj?.primaryCtaLink &&
    props?.modelObj?.primaryCtaText.length > 0 &&
    props?.modelObj?.primaryCtaLink.length > 0;
  const hasSecondaryButton =
    props?.modelObj?.secondaryCtaText &&
    props?.modelObj?.secondaryCtaLink &&
    props?.modelObj?.secondaryCtaText.length > 0 &&
    props?.modelObj?.secondaryCtaLink.length > 0;

  const primaryCtaOpenInNewTab = props?.modelObj?.primaryCtaOpenInNewTab;
  const secondaryCtaOpenInNewTab = props?.modelObj?.secondaryCtaOpenInNewTab;

  const hasStandard = styleclass?.includes('standard');
  const hasColorScheme = styleclass?.split(' ').some((cls: string) => cls.startsWith('color-scheme'));

  return (
    <StyledOffersHeroMBE
      data-component-name="m-offers-mbe-hero"
      data-testid="offers-mbe-hero"
      scrimGradient={styleclass}
      dir={dir}
      className={clsx('row', 'hero', 'm-container-fullbleed', {
        [styleclass]: hasStandard || hasColorScheme,
        inverse: !hasStandard && !hasColorScheme,
      })}
    >
      <div className={clsx('hero_item hero-img-wrapper')}>
        <div className={clsx('d-sm-block hero-img-container')}>
          <Image
            customClass="feature-img"
            title={imageAltText ?? imageTitle}
            defaultImageURL={desktopImageSrc}
            loading="eager"
          />
        </div>
        <div
          className={`hero__cnt-sec ${
            !(header || description || props?.modelObj?.primaryCtaText || props?.modelObj?.secondaryCtaText)
              ? ''
              : 'gradient'
          }`}
        >
          <div className="container-sm hero__cnt-ctr">
            <div className="row">
              <div className="hero_content col-12 col-sm-6 col-lg-6">
                <Heading
                  customClass={`hero_head ${mQuery === true ? 'm-ellipsis-3lines' : 'm-ellipsis-2lines'}`}
                  titleText={header}
                  variation={Types.headingType.title}
                  fontSize={Types.size.medium}
                  element={headerTag}
                />
                {description && (
                  <p
                    className={clsx('hero_desc t-font-m m-ellipsis-2lines')}
                    dangerouslySetInnerHTML={{ __html: description }}
                  ></p>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-8 col-lg-12">
                <div className="hero_cta-wrapper hero_cta-center">
                  {hasPrimaryButton && !(props?.modelObj?.primaryCtaHide === true && isUserLoggedIn) && (
                    <Button
                      isLink
                      href={props?.modelObj?.primaryCtaLink}
                      className={`mb-2 hero_cta m-button-primary ${mQuery === true ? 'm-button-s' : 'm-button-m'} ${
                        primaryCtaOpenInNewTab === true ? 'm-button-external' : ''
                      }`}
                      target={primaryCtaOpenInNewTab === true ? '_blank' : ''}
                      trackingProperties={props?.modelObj?.trackingProperties}
                      ref={CardLinkPrimaryBtnRef}
                    >
                      {props?.modelObj?.primaryCtaText}
                    </Button>
                  )}
                  {hasSecondaryButton && !(props?.modelObj?.secondaryCtaHide === true && isUserLoggedIn) && (
                    <Button
                      isLink
                      href={props?.modelObj?.secondaryCtaLink}
                      className={`hero_cta ${
                        hasPrimaryButton && !(props?.modelObj?.primaryCtaHide === true && isUserLoggedIn)
                          ? 'hero_sec-cta'
                          : ''
                      } m-button-secondary ${mQuery === true ? 'm-button-s' : 'm-button-m'} ${
                        secondaryCtaOpenInNewTab === true ? 'm-button-external' : ''
                      }`}
                      target={secondaryCtaOpenInNewTab === true ? '_blank' : ''}
                      custom_click_track_value={`${header}|${props?.modelObj?.secondaryCtaText}|${
                        secondaryCtaOpenInNewTab === true ? 'external' : 'internal'
                      }`}
                    >
                      {props?.modelObj?.secondaryCtaText}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StyledOffersHeroMBE>
  );
};

export default OffersMBE;
